import { useSyncExternalStore } from 'react';
import { onlineManager } from '@tanstack/react-query';

/**
 * Whether the browser believes it has a connection
 * (docs/07-implementation-roadmap.md F7.5).
 *
 * **Read from TanStack Query's `onlineManager` rather than from
 * `navigator.onLine` directly**, deliberately. That manager is not a convenience
 * wrapper here — it is the thing that *acts* on the answer: with the default
 * `networkMode: 'online'`, a query started while offline does not fail, it
 * **pauses**, and a mutation submitted while offline waits and then runs when
 * the connection returns. Reading a second, independent signal would let the
 * banner say "connected" while every query on the screen is still parked, or the
 * reverse — a UI disagreeing with the data layer about the one fact both are
 * describing.
 *
 * `navigator.onLine` is what the manager watches, and it is worth knowing what
 * it means: **false is reliable, true is a guess.** The browser says `false`
 * when the machine has no route to a network at all, which is the case this hook
 * catches — a dropped office wifi. It says `true` for a connected wifi with a
 * dead uplink, which is what the church's connection actually does on a bad day.
 * So this is one of two signals, and the other is a request that failed with
 * `kind: 'network'` while `onLine` is true. Screens show that one through
 * `QueryErrorAlert`; both end up saying "check the connection".
 */
export function useIsOnline(): boolean {
  return useSyncExternalStore(
    (listener) => onlineManager.subscribe(listener),
    () => onlineManager.isOnline(),
    // Server-rendered output would be built without a browser to ask, and an
    // app that assumes it is offline until proven otherwise flashes a banner on
    // every load. This app is not server-rendered today; the value is here so
    // the hook is not the thing that breaks if it ever is.
    () => true,
  );
}
